<template>
  <div class="admin-page">
    <div class="admin-header">
      <div>
        <router-link to="/pointe/admin/consultations" class="back-link">← Consultations</router-link>
        <h1>{{ isEdit ? 'Edit Consultation' : 'New Foot 360 Consultation' }}</h1>
      </div>
    </div>

    <div v-if="loading" class="loading-wrap">
      <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
    </div>

    <form v-else @submit.prevent="save" class="consult-form">
      <!-- Basic info -->
      <div class="form-section">
        <h2>Dancer Information</h2>
        <div class="form-row">
          <div class="form-group">
            <label>Dancer Name *</label>
            <input v-model="form.dancerName" type="text" required class="form-input" />
          </div>
          <div class="form-group">
            <label>Dancer Email *</label>
            <input v-model="form.dancerEmail" type="email" required class="form-input" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Consultation Date *</label>
            <input v-model="form.consultationDate" type="date" required class="form-input" />
          </div>
          <div class="form-group">
            <label>Status</label>
            <select v-model="form.status" class="form-input">
              <option value="draft">Draft</option>
              <option value="complete">Complete</option>
            </select>
          </div>
        </div>
        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.isHistorical" />
            Historical record (work done before this app)
          </label>
        </div>
      </div>

      <!-- Links -->
      <div class="form-section">
        <h2>Resources & Links</h2>
        <div class="form-group">
          <label>Google Doc Analysis Link</label>
          <input v-model="form.googleDocLink" type="url" class="form-input" placeholder="https://docs.google.com/…" />
        </div>
        <div class="form-group">
          <label>Loom Video Link</label>
          <input v-model="form.loomLink" type="url" class="form-input" placeholder="https://www.loom.com/…" />
        </div>
        <div class="form-group">
          <label>Practice Plan Google Doc Link</label>
          <input v-model="form.practiceGoogleDocLink" type="url" class="form-input" placeholder="https://docs.google.com/…" />
        </div>
        <div v-if="form.isHistorical" class="form-group">
          <label>PDF Link (historical record)</label>
          <input v-model="form.pdfUrl" type="url" class="form-input" placeholder="https://drive.google.com/…" />
        </div>
      </div>

      <!-- Follow-ups -->
      <div class="form-section">
        <h2>Follow-up Tracking</h2>
        <div class="form-row">
          <div class="form-group">
            <label>Follow-ups Remaining</label>
            <input v-model.number="form.followUpsRemaining" type="number" min="0" class="form-input" />
          </div>
          <div class="form-group">
            <label>Follow-ups Used</label>
            <input v-model.number="form.followUpsUsed" type="number" min="0" class="form-input" />
          </div>
        </div>
      </div>

      <!-- Criteria checklist -->
      <div class="form-section">
        <h2>Criteria Assessment</h2>
        <p class="section-hint">Check each criterion and add notes. Photos are stored as URLs (upload separately and paste link).</p>

        <div v-if="criteria.length === 0" class="empty-state">
          <p>No active criteria. <router-link to="/pointe/admin/criteria">Add criteria →</router-link></p>
        </div>

        <div v-for="criterion in criteria" :key="criterion.id" class="criterion-item">
          <div class="criterion-header">
            <button
              type="button"
              class="toggle-btn"
              :class="{ pass: criteriaResults[criterion.id] === true, fail: criteriaResults[criterion.id] === false }"
              @click="cycleCriterion(criterion.id)"
            >
              <span v-if="criteriaResults[criterion.id] === true">✓ Pass</span>
              <span v-else-if="criteriaResults[criterion.id] === false">✗ Needs Work</span>
              <span v-else>— Not Reviewed</span>
            </button>
            <span class="criterion-name">{{ criterion.title }}</span>
            <span v-if="criterion.category" class="criterion-cat">{{ criterion.category }}</span>
          </div>
          <div class="criterion-fields">
            <textarea
              v-model="criteriaNotes[criterion.id]"
              class="notes-input"
              rows="2"
              placeholder="Notes…"
            ></textarea>
            <input
              v-model="criteriaPhotos[criterion.id]"
              type="url"
              class="form-input photo-input"
              placeholder="Photo URL (optional)…"
            />
          </div>
        </div>
      </div>

      <!-- Questionnaire completed -->
      <div class="form-section">
        <h2>Questionnaire</h2>
        <label class="checkbox-label">
          <input type="checkbox" v-model="form.questionnaireCompleted" />
          Dancer has completed the Foot 360 history questionnaire
        </label>
      </div>

      <!-- Overall result -->
      <div class="form-section">
        <h2>Overall Result</h2>
        <div class="result-buttons">
          <button type="button" class="result-btn approved" :class="{ selected: form.overallApproved === true }" @click="form.overallApproved = true">Approved</button>
          <button type="button" class="result-btn needs-work" :class="{ selected: form.overallApproved === false }" @click="form.overallApproved = false">Not Approved</button>
          <button type="button" class="result-btn" :class="{ selected: form.overallApproved === null }" @click="form.overallApproved = null">Pending</button>
        </div>
      </div>

      <div class="form-group">
        <label>Admin Notes</label>
        <textarea v-model="form.adminNotes" class="form-input form-textarea" rows="3"></textarea>
      </div>

      <p v-if="error" class="error-text">{{ error }}</p>

      <div class="form-actions">
        <router-link to="/pointe/admin/consultations" class="btn-secondary">Cancel</router-link>
        <button type="submit" class="btn-primary" :disabled="saving">
          {{ saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Consultation') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  data () {
    return {
      criteria: [],
      criteriaResults: {},
      criteriaNotes: {},
      criteriaPhotos: {},
      loading: true,
      saving: false,
      error: '',
      form: {
        dancerName: '',
        dancerEmail: '',
        consultationDate: '',
        status: 'draft',
        isHistorical: false,
        googleDocLink: '',
        loomLink: '',
        practiceGoogleDocLink: '',
        pdfUrl: '',
        followUpsRemaining: 2,
        followUpsUsed: 0,
        questionnaireCompleted: false,
        overallApproved: null,
        adminNotes: ''
      }
    }
  },
  computed: {
    isEdit () {
      return !!this.$route.query.edit
    }
  },
  async created () {
    const criteriaSnap = await db.collection('pointe-criteria').where('active', '==', true).orderBy('order').get()
    this.criteria = criteriaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    this.criteria.forEach(c => {
      this.$set(this.criteriaResults, c.id, null)
      this.$set(this.criteriaNotes, c.id, '')
      this.$set(this.criteriaPhotos, c.id, '')
    })
    if (this.isEdit) {
      const doc = await db.collection('pointe-consultations').doc(this.$route.query.edit).get()
      if (doc.exists) {
        const d = doc.data()
        this.form = {
          dancerName: d.dancerName || '',
          dancerEmail: d.dancerEmail || '',
          consultationDate: this.tsToDateInput(d.consultationDate),
          status: d.status || 'draft',
          isHistorical: d.isHistorical || false,
          googleDocLink: d.googleDocLink || '',
          loomLink: d.loomLink || '',
          practiceGoogleDocLink: d.practiceGoogleDocLink || '',
          pdfUrl: d.pdfUrl || '',
          followUpsRemaining: d.followUpsRemaining !== undefined ? d.followUpsRemaining : 2,
          followUpsUsed: d.followUpsUsed || 0,
          questionnaireCompleted: d.questionnaireCompleted || false,
          overallApproved: d.overallApproved !== undefined ? d.overallApproved : null,
          adminNotes: d.adminNotes || ''
        }
        const existing = d.criteria || {}
        for (const [cid, val] of Object.entries(existing)) {
          this.$set(this.criteriaResults, cid, val.passed)
          this.$set(this.criteriaNotes, cid, val.adminNotes || '')
          this.$set(this.criteriaPhotos, cid, val.photoUrl || '')
        }
      }
    }
    this.loading = false
  },
  methods: {
    tsToDateInput (ts) {
      if (!ts) return ''
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toISOString().slice(0, 10)
    },
    cycleCriterion (id) {
      const current = this.criteriaResults[id]
      if (current === undefined || current === null) {
        this.$set(this.criteriaResults, id, true)
      } else if (current === true) {
        this.$set(this.criteriaResults, id, false)
      } else {
        this.$set(this.criteriaResults, id, null)
      }
    },
    buildCriteriaObj () {
      const obj = {}
      this.criteria.forEach(c => {
        obj[c.id] = {
          passed: this.criteriaResults[c.id] !== undefined ? this.criteriaResults[c.id] : null,
          adminNotes: this.criteriaNotes[c.id] || '',
          photoUrl: this.criteriaPhotos[c.id] || ''
        }
      })
      return obj
    },
    async save () {
      this.saving = true
      this.error = ''
      try {
        const data = {
          dancerName: this.form.dancerName.trim(),
          dancerEmail: this.form.dancerEmail.toLowerCase().trim(),
          consultationDate: new Date(this.form.consultationDate),
          status: this.form.status,
          isHistorical: this.form.isHistorical,
          googleDocLink: this.form.googleDocLink.trim(),
          loomLink: this.form.loomLink.trim(),
          practiceGoogleDocLink: this.form.practiceGoogleDocLink.trim(),
          pdfUrl: this.form.pdfUrl.trim(),
          followUpsRemaining: Number(this.form.followUpsRemaining),
          followUpsUsed: Number(this.form.followUpsUsed),
          questionnaireCompleted: this.form.questionnaireCompleted,
          overallApproved: this.form.overallApproved,
          adminNotes: this.form.adminNotes.trim(),
          criteria: this.buildCriteriaObj()
        }
        if (this.isEdit) {
          await db.collection('pointe-consultations').doc(this.$route.query.edit).update(data)
        } else {
          await db.collection('pointe-consultations').add({
            ...data,
            linkedApplicationIds: [],
            createdAt: new Date()
          })
        }
        this.$router.push('/pointe/admin/consultations')
      } catch (e) {
        this.error = e.message
      }
      this.saving = false
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.admin-header {
  margin-bottom: 28px;
}

.admin-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 26px;
  color: #1a1a1a;
  margin: 4px 0 0;
}

.back-link { font-size: 13px; color: #888; text-decoration: none; display: block; margin-bottom: 4px; }
.back-link:hover { color: #1a1a1a; }

.loading-wrap { padding: 40px; text-align: center; }

.consult-form { display: flex; flex-direction: column; gap: 0; }

.form-section {
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 20px;
  background: #fff;
}

.form-section h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  color: #1a1a1a;
  margin: 0 0 16px;
}

.section-hint { font-size: 13px; color: #888; margin: -10px 0 14px; }

.form-row { display: flex; gap: 16px; }
.form-row .form-group { flex: 1; }

.form-group { margin-bottom: 16px; }

.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: #1a1a1a;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 10px 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  border-radius: 2px;
  box-sizing: border-box;
  color: #1a1a1a;
  background: #fff;
}

.form-input:focus { outline: none; border-color: #1a1a1a; }
.form-textarea { resize: vertical; }

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 15px;
}

.empty-state { padding: 20px; text-align: center; color: #888; }

.criterion-item {
  border: 1px solid #f0eeec;
  border-radius: 4px;
  padding: 12px 14px;
  margin-bottom: 8px;
  background: #faf9f7;
}

.criterion-header { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }

.criterion-name { font-size: 15px; font-weight: 600; color: #1a1a1a; flex: 1; }
.criterion-cat { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: .05em; }

.toggle-btn {
  border: none;
  padding: 5px 12px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  background: #f0f0f0;
  color: #888;
  white-space: nowrap;
}

.toggle-btn.pass { background: #e8f5e9; color: #2e7d32; }
.toggle-btn.fail { background: #fce4ec; color: #c62828; }

.criterion-fields { display: flex; flex-direction: column; gap: 6px; }

.notes-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 7px 10px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  border-radius: 2px;
  box-sizing: border-box;
  resize: vertical;
  background: #fff;
  color: #1a1a1a;
}

.notes-input:focus { outline: none; border-color: #1a1a1a; }

.photo-input { font-size: 13px; padding: 7px 10px; }

.result-buttons { display: flex; gap: 12px; }

.result-btn {
  flex: 1;
  padding: 12px;
  border: 2px solid #e8e5e1;
  border-radius: 4px;
  background: #fff;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.15s;
}

.result-btn.approved.selected { border-color: #2e7d32; background: #e8f5e9; color: #2e7d32; }
.result-btn.needs-work.selected { border-color: #c62828; background: #fce4ec; color: #c62828; }
.result-btn.selected { border-color: #1a1a1a; background: #f0f0f0; }

.form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 8px; }

.btn-primary {
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
}

.btn-primary:hover { background: #333; }
.btn-primary:disabled { opacity: 0.5; cursor: default; }

.btn-secondary {
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  text-decoration: none;
  display: inline-block;
  border-radius: 2px;
}

.error-text { color: #c00; font-size: 14px; }
</style>
